import React, { useState, useEffect, useRef } from 'react'
import SingleSlider from './SingleSlider'
import IntegerSliderEntry from './IntegerSliderEntry'
import HighLimitSliderEntry from './HighLimitSliderEntry'
import StringStatEntry from './StringStatInput'
import { useLoadData } from '../Load/LoadDataProvider'

interface SliderEntryProps {
	onSubmit: (values: Record<string, number | string>) => void
	is121Plus: boolean
	setIs121Plus: (value: boolean) => void
}

interface SliderValues {
	hardness: number
	density: number
	flexibility: number
	durability: number
	mining_speed: number
	mining_level: number | string
}

enum CustomSliderType {
	INTEGER,
	FLOAT,
	STRING
}

const SliderEntry: React.FC<SliderEntryProps> = ({ onSubmit, is121Plus }) => {
	const [sliderValues, setSliderValues] = useState<SliderValues>({
		hardness: 6,
		density: 2,
		flexibility: 3,
		durability: 256,
		mining_level: 2,
		mining_speed: 5
	})

	const [customSliders, setCustomSliders] = useState<{
		[id: string]: {
			name: string
			value: any
			type: CustomSliderType
			id: string
		}
	}>({})

	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownButtonRef = useRef<HTMLButtonElement>(null)
	const { loadData } = useLoadData()

	useEffect(() => {
		const excludeKeys: string[] = ['key', 'translation', 'fake_translation']
		const baseSlider: string[] = is121Plus
			? ['hardness', 'flexibility', 'density', 'durability', 'mining_speed', 'mining_level']
			: ['hardness', 'flexibility', 'density', 'durability', 'tier', 'mining_speed', 'mining_level']

		if (loadData != null) {
			const excludedKeys = new Set(['key', 'translation', 'fake_translation', 'color', 'color_palette', 'icon', 'items', 'groups'])
			const entries = Object.entries(loadData).filter(([key]) => !excludedKeys.has(key))
			const filteredEntries = entries.filter(([key]) => !excludeKeys.includes(key))
			const baseSliders = filteredEntries.filter(([key]) => baseSlider.includes(key))
			const customEntries = filteredEntries.filter(([key]) => !baseSlider.includes(key))

			baseSliders.forEach(([key, value]) => {
				if (typeof value === 'number' || typeof value === 'string') {
					handleSliderChange(key, value)
				}
			})

			const sliders: Record<string, { id: string; name: string; value: any; type: CustomSliderType }> = {}
			const usedNames = new Set<string>(baseSlider)

			customEntries.forEach(([key, value]) => {
				if (typeof value !== 'number' && typeof value !== 'string') {
					return
				}

				if (usedNames.has(key)) {
					return
				}

				usedNames.add(key)

				let uuid
				do {
					uuid = generateId()
				} while (sliders[uuid] !== undefined)
				sliders[uuid] = {
					id: uuid,
					name: key,
					value,
					type: typeof value === 'number' ? CustomSliderType.FLOAT : CustomSliderType.STRING
				}
			})

			setCustomSliders(sliders)
		}
	}, [loadData, is121Plus])

	const handleSliderChange = (sliderName: string, newValue: number | string) => {
		setSliderValues((prevValues) => ({
			...prevValues,
			[sliderName]: newValue
		}))
	}

	useEffect(() => {
		setCustomSliders((prev) => {
			let sliders = { ...prev }

			const existing = Object.values(sliders).find((s) => s.name === 'toughness')

			if (is121Plus) {
				if (!existing) {
					let uuid
					do {
						uuid = generateId()
					} while (sliders[uuid] !== undefined)

					sliders[uuid] = {
						id: uuid,
						name: 'toughness',
						value: 0,
						type: CustomSliderType.FLOAT
					}
				}
			} else {
				// remove it if switching back
				sliders = Object.fromEntries(Object.entries(sliders).filter(([_, s]) => s.name !== 'toughness'))
			}

			return sliders
		})
	}, [is121Plus])

	useEffect(() => {
		handleEntrySubmit()
	}, [sliderValues, customSliders])

	useEffect(() => {
		const loadedMiningLevel = loadData?.mining_level

		if (loadedMiningLevel !== undefined) {
			handleSliderChange('mining_level', is121Plus ? String(loadedMiningLevel) : Number(loadedMiningLevel))
			return
		}

		handleSliderChange('mining_level', is121Plus ? 'minecraft:incorrect_for_wooden_tool' : 2)
	}, [is121Plus, loadData])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && !dropdownButtonRef.current?.contains(event.target as Node)) {
				setIsDropdownOpen(false)
			}
		}

		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		} else {
			document.removeEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isDropdownOpen])

	const handleEntrySubmit = () => {
		const customSliderValues: { [name: string]: number | string } = {}
		Object.values(customSliders).forEach((slider) => {
			customSliderValues[slider.name] = slider.value
		})
		onSubmit({ ...sliderValues, ...customSliderValues })
	}

	const renderCustomSliders = () => {
		return Object.values(customSliders).map((slider) => {
			let sliderComponent
			switch (slider.type) {
				case CustomSliderType.FLOAT:
					sliderComponent = (
						<SingleSlider
							label={slider.name}
							onChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										value
									}
								}))
							}}
							value={slider.value}
							editableLabel={true}
							onLabelChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										name: value
									}
								}))
							}}
						/>
					)
					break
				case CustomSliderType.INTEGER:
					sliderComponent = (
						<IntegerSliderEntry
							label={slider.name}
							value={slider.value}
							onChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										value
									}
								}))
							}}
							editableLabel={true}
							onLabelChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										name: value
									}
								}))
							}}
						/>
					)
					break
				case CustomSliderType.STRING:
					sliderComponent = (
						<StringStatEntry
							label={slider.name}
							value={slider.value}
							onChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										value
									}
								}))
							}}
							editableLabel={true}
							onLabelChange={(value) => {
								setCustomSliders((prev) => ({
									...prev,
									[slider.id]: {
										...slider,
										name: value
									}
								}))
							}}
						/>
					)
					break
			}

			return (
				<div
					key={slider.id}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '10px',
						background: 'var(--input-bg)',
						border: '1px solid var(--input-border)',
						borderRadius: '9px',
						padding: '10px 12px'
					}}
				>
					<button
						type="button"
						onClick={() => {
							setCustomSliders((prevSliders) => {
								const newSliders = { ...prevSliders }
								delete newSliders[slider.id]
								return newSliders
							})
						}}
						style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex' }}
					>
						<DeleteIcon />
					</button>
					{sliderComponent}
				</div>
			)
		})
	}

	function generateId() {
		return '_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
	}

	return (
		<div>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
				<SingleSlider label="Hardness" value={sliderValues.hardness} onChange={(value) => handleSliderChange('hardness', value)} />
				<SingleSlider label="Density" value={sliderValues.density} onChange={(value) => handleSliderChange('density', value)} />
				<SingleSlider label="Flexibility" value={sliderValues.flexibility} onChange={(value) => handleSliderChange('flexibility', value)} />
				<HighLimitSliderEntry label="Durability" value={sliderValues.durability} onChange={(value) => handleSliderChange('durability', value)} />
				<SingleSlider label="Mining Speed" value={sliderValues.mining_speed} onChange={(value) => handleSliderChange('mining_speed', value)} />
				{is121Plus ? (
					<StringStatEntry
						label="Mining Level"
						value={String(sliderValues.mining_level)}
						onChange={(value) => handleSliderChange('mining_level', value)}
						editableLabel={false}
					/>
				) : (
					<IntegerSliderEntry
						label="Mining Level"
						value={typeof sliderValues.mining_level === 'number' ? sliderValues.mining_level : 2}
						onChange={(value) => handleSliderChange('mining_level', value)}
					/>
				)}
			</div>

			<div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '18px' }}>{renderCustomSliders()}</div>

			<div style={{ position: 'relative', marginTop: '12px' }}>
				<button
					ref={dropdownButtonRef}
					onClick={(e) => {
						e.preventDefault()
						setIsDropdownOpen(!isDropdownOpen)
					}}
					style={{
						width: '100%',
						padding: '9px',
						borderRadius: '8px',
						border: '1px dashed var(--input-border)',
						background: 'var(--input-bg)',
						color: 'var(--text-secondary)',
						cursor: 'pointer',
						fontSize: '13px',
						fontWeight: 600
					}}
				>
					+ Add custom stat
				</button>
				{isDropdownOpen && (
					<div
						ref={dropdownRef}
						style={{
							position: 'absolute',
							top: 'calc(100% + 6px)',
							left: 0,
							right: 0,
							zIndex: 10,
							background: 'var(--input-bg)',
							border: '1px solid var(--input-border)',
							borderRadius: '8px',
							overflow: 'hidden',
							boxShadow: '0 10px 24px rgba(0, 0, 0, 0.4)'
						}}
					>
						{Object.keys(CustomSliderType)
							.filter((key) => isNaN(Number(key)))
							.map((key) => (
								<div
									key={key}
									onClick={() => {
										let uuid
										do {
											uuid = generateId()
										} while (customSliders[uuid] !== undefined)
										setCustomSliders({
											...customSliders,
											[uuid]: {
												id: uuid,
												name: 'new',
												value: key === 'STRING' ? '' : 0,
												type: CustomSliderType[key as keyof typeof CustomSliderType]
											}
										})
										setIsDropdownOpen(false)
									}}
									className="dropdown-item"
									style={{ textTransform: 'capitalize' }}
								>
									{key.toLowerCase()}
								</div>
							))}
					</div>
				)}
			</div>
		</div>
	)
}

export default SliderEntry

const DeleteIcon = (props: any) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="lucide lucide-trash-2"
	>
		<path d="M3 6h18" />
		<path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
		<path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
		<line x1="10" x2="10" y1="11" y2="17" />
		<line x1="14" x2="14" y1="11" y2="17" />
	</svg>
)
